import { ApplyEditWaypointsChangesProperties } from "@/analytics/edit-waypoints-tool-properties";
import { updateProject } from "@/components/common/project-provider/update-project";
import { IELEMENTS_REQUEST_CHUNK_SIZE } from "@/components/common/project-provider/use-project-initial-data";
import {
  enableLocationMarkerCreation,
  selectLocationMarkerArea,
} from "@/store/location-marker-slice";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import { useToast } from "@faro-lotv/flat-ui";
import { assert, generateGUID, GUID } from "@faro-lotv/foundation";
import {
  createIElement,
  IElementRoomSection,
  IElementRoomsGroup,
  IElementTimeSeriesRoom,
  IElementType,
  IElementTypeHint,
  IPose,
  isIElementAreaSection,
  isIElementWithTypeAndHint,
  isValid,
} from "@faro-lotv/ielement-types";
import {
  selectChildDepthFirst,
  selectIElement,
  TransformOverrides,
} from "@faro-lotv/project-source";
import {
  createMutationAddRoomOnSheet,
  createMutationSetElementPosition,
  Mutation,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { chunk } from "es-toolkit";
import { useCallback, useMemo } from "react";
import { Vector3 } from "three";
import { temporal, TemporalState } from "zundo";
import { create, useStore } from "zustand";
import { shallow } from "zustand/shallow";

export type EditWaypointsState = {
  /**
   * The overrides for element transforms. In Project API local space.
   * Allows undefined as a value to improve type-safety, since not all keys have to have an override defined.
   */
  transformOverrides: Record<GUID, IPose | undefined>;

  /** Add a new position override for an element. In Project API local space. */
  setTransformOverride(id: GUID, pose: IPose): void;

  /** The id of the location marker currently being edited, if any. */
  editingLocationMarkerId: GUID | undefined;

  /** True if performing edit right after location marker creation. */
  editingRightAfterCreation: boolean;

  /** Sets the id of the location marker currently being edited. */
  setEditingLocationMarkerId(id: GUID | undefined): void;

  /**
   * The newly created location markers
   */
  newLocationMarkers: IElementRoomSection[];

  /** Add a newly created location marker */
  addNewLocationMarker(locationMarker: IElementRoomSection): void;

  /** Update new location markers */
  updateNewLocationMarkers(locationMarkers: IElementRoomSection[]): void;
};

/** Returns the zustand store for the waypoint editing tool. Uses zundo, so actions should map into un-doable steps. */
export const useEditWaypointsStore = create<EditWaypointsState>()(
  temporal(
    (set) => ({
      transformOverrides: {},
      editingLocationMarkerId: undefined,
      editingRightAfterCreation: false,
      newLocationMarkers: [],
      setTransformOverride(id: GUID, pose: IPose) {
        set((state) => ({
          transformOverrides: {
            ...state.transformOverrides,
            [id]: pose,
          },
        }));
      },
      addNewLocationMarker(locationMarker: IElementRoomSection) {
        set((state) => ({
          newLocationMarkers: [...state.newLocationMarkers, locationMarker],
          editingLocationMarkerId: locationMarker.id,
          editingRightAfterCreation: true,
        }));
      },
      updateNewLocationMarkers(newLocationMarkers: IElementRoomSection[]) {
        set({
          newLocationMarkers,
        });
      },
      setEditingLocationMarkerId(id: GUID | undefined) {
        set({
          editingLocationMarkerId: id,
          editingRightAfterCreation: false,
        });
      },
    }),
    {
      // Remove props from the undo/redo history that are not relevant
      partialize: ({
        // eslint-disable-next-line unused-imports/no-unused-vars
        editingLocationMarkerId,
        // eslint-disable-next-line unused-imports/no-unused-vars
        editingRightAfterCreation,
        ...rest
      }) => rest,
      equality: (pastState, currState) => shallow(pastState, currState),
    },
  ),
);

/** @returns The TransformOverrides compatible with the iElements store */
export function useEditWaypointsTransformOverrides(): TransformOverrides {
  const { transformOverrides: transformOverridesLocal } =
    useEditWaypointsStore();
  return useMemo(
    () => ({
      local: transformOverridesLocal,
      global: {},
    }),
    [transformOverridesLocal],
  );
}

/**
 * @returns Functions and state to control undo/redo state for the waypoint editing tool.
 */
export function useEditWaypointsUndoRedo(): TemporalState<
  Omit<
    EditWaypointsState,
    "editingLocationMarkerId" | "editingRightAfterCreation"
  >
> {
  return useStore(useEditWaypointsStore.temporal);
}

/**
 * @returns Function to reset the store and clear all undo history.
 */
export function useResetEditWaypointsStore(): () => void {
  const dispatch = useAppDispatch();
  const { clear } = useEditWaypointsUndoRedo();
  return useCallback(() => {
    useEditWaypointsStore.setState(
      useEditWaypointsStore.getInitialState(),
      true,
    );
    clear();
    dispatch(enableLocationMarkerCreation(false));
  }, [clear, dispatch]);
}

/**
 * @returns A Function to save all pending changes in the edit waypoints store to the project.
 * Resets the store after saving.
 */
export function useApplyChangesInEditWaypointsStore(): () => Promise<void> {
  const dispatch = useAppDispatch();
  const { projectApiClient } = useApiClientContext();

  const resetEditWaypointsStore = useResetEditWaypointsStore();

  const { openToast } = useToast();

  const areaId = useAppSelector(selectLocationMarkerArea);

  const store = useAppStore();

  return useCallback(async () => {
    const { mutations, movedElementIds } = createMutationsForWaypointMoves();

    const locationMarkerMutations = createMutationsForLocationMarkers(
      store.getState(),
      areaId,
    );

    if (!mutations.length && !locationMarkerMutations.length) return;

    await projectApiClient.applyMutations([
      ...mutations,
      ...locationMarkerMutations,
    ]);
    const updatedElementsIds = locationMarkerMutations.length
      ? [...movedElementIds, areaId]
      : movedElementIds;
    for (const idChunk of chunk(
      updatedElementsIds,
      IELEMENTS_REQUEST_CHUNK_SIZE,
    )) {
      await dispatch(
        updateProject({
          projectApi: projectApiClient,
          iElementQuery: {
            ancestorIds: idChunk,
          },
        }),
      );
    }

    resetEditWaypointsStore();

    openToast({
      title: "Waypoint changes applied",
    });
  }, [
    areaId,
    dispatch,
    openToast,
    projectApiClient,
    resetEditWaypointsStore,
    store,
  ]);
}

type MutationsForWaypointMoves = {
  /** The mutations to apply to apply the waypoints move in the store. */
  mutations: Mutation[];

  /** The elements that have been moved and should be updated in the project after the mutation. */
  movedElementIds: GUID[];
};

/** @returns a list of mutations that applies the position overrides to fitting elements in the project. */
function createMutationsForWaypointMoves(): MutationsForWaypointMoves {
  const { transformOverrides } = useEditWaypointsStore.getState();

  const mutations: Mutation[] = [];
  const movedElementIds: GUID[] = [];

  for (const [elementId, transformOverride] of Object.entries(
    transformOverrides,
  )) {
    if (!transformOverride) continue;

    mutations.push(
      createMutationSetElementPosition(elementId, transformOverride.pos),
    );
    movedElementIds.push(elementId);
  }

  return { mutations, movedElementIds };
}

function createMutationsForLocationMarkers(
  storeState: RootState,
  areaId: GUID,
): Mutation[] {
  const { newLocationMarkers } = useEditWaypointsStore.getState();

  if (!newLocationMarkers.length) return [];

  let newGroupId: GUID | undefined;
  return newLocationMarkers.map((locationMarker) => {
    assert(locationMarker.pose?.pos);

    const newMutation = selectMutationForNewRoomSection({
      id: locationMarker.id,
      name: locationMarker.name,
      worldPos: new Vector3(
        locationMarker.pose.pos.x,
        locationMarker.pose.pos.y,
        locationMarker.pose.pos.z,
      ),
      areaId,
      newGroupId,
    })(storeState);
    newGroupId = newGroupId ?? newMutation.group?.id;

    return newMutation;
  });
}

type SelectMutationForNewRoomSectionProps = {
  /** The id of the room section to create. */
  id: GUID;

  /** The name of the room section */
  name: string;

  /** The area to create the room section in. */
  areaId: GUID;

  /** The world position of the room section */
  worldPos: Vector3;

  /**
   * - if the rooms group is missing:
   *  - the id of the new group once it has been created locally by previous call to selectMutationForNewRoomSection
   *  - undefined if the missing group has not been created yet
   * - ignored when the group already exists
   */
  newGroupId?: GUID;
};

/** @returns the mutation for a single new room section */
export function selectMutationForNewRoomSection({
  id,
  name,
  worldPos,
  areaId,
  newGroupId,
}: SelectMutationForNewRoomSectionProps) {
  return (state: RootState) => {
    const area = selectIElement(areaId)(state);
    assert(
      area && isIElementAreaSection(area),
      "Area for location markers must exist.",
    );
    const roomsGroup = selectChildDepthFirst(area, (el) =>
      isIElementWithTypeAndHint(el, IElementType.group, IElementTypeHint.rooms),
    )(state);

    const userId = selectCurrentUser(state)?.id ?? "";
    const parentWorldMatrixInvert = selectIElementWorldMatrix4(
      roomsGroup?.id ?? newGroupId ?? areaId,
    )(state)
      .clone()
      .invert();

    const { rootId } = area;
    const groupId = roomsGroup ? roomsGroup.id : (newGroupId ?? generateGUID());
    const pos = new Vector3();

    // calculate position of the location marker in parent coordinate system
    pos.copy(worldPos).applyMatrix4(parentWorldMatrixInvert);

    const timeSeriesRoom = createIElement<IElementTimeSeriesRoom>({
      id: generateGUID(),
      type: IElementType.timeSeries,
      typeHint: IElementTypeHint.room,
      rootId,
      name,
      createdBy: userId,
      parentId: id,
    });
    const newRoomSection: IElementRoomSection =
      createIElement<IElementRoomSection>({
        type: IElementType.section,
        typeHint: IElementTypeHint.room,
        id,
        name,
        rootId,
        createdBy: userId,
        parentId: groupId,
        childrenIds: [timeSeriesRoom.id],
        pose: {
          // right to left hand
          // y value must be zero to make MutationAddRoomOnSheet valid (position on area plane)
          pos: { x: pos.x, y: 0, z: -pos.z },
          scale: null,
          rot: null,
          gps: null,
          isWorldRot: false,
        },
      });

    // If rooms group does not exist yet, create a new room group and use area id as target element id
    // If rooms group already exists, use the group id as target element id and no need to provide group for the mutation
    const newGroup =
      !roomsGroup && !newGroupId
        ? createIElement<IElementRoomsGroup>({
            id: groupId,
            type: IElementType.group,
            typeHint: IElementTypeHint.rooms,
            rootId,
            name: "Rooms",
            createdBy: userId,
            parentId: area.id,
            childrenIds: [newRoomSection.id],
            xOr: false,
          })
        : undefined;
    const parentId = !roomsGroup && !newGroupId ? area.id : groupId;

    return createMutationAddRoomOnSheet(
      parentId,
      timeSeriesRoom,
      newRoomSection,
      newGroup,
    );
  };
}

/**
 * @returns whether the edit waypoints state has unsaved changes
 * @param state The current state of the edit waypoints store
 */
export function selectHasEditWaypointsUnsavedChanges(
  state: EditWaypointsState,
): boolean {
  return (
    Object.values(state.transformOverrides).some(isValid) ||
    state.newLocationMarkers.length > 0
  );
}

/**
 * @returns Stats about the edits for the applyEditWaypointsChanges event.
 * @param state The current state of the edit waypoints store
 */
export function selectEditWaypointsAnalyticsStats(
  state: EditWaypointsState,
): Omit<ApplyEditWaypointsChangesProperties, "via"> {
  return {
    numWaypointsMoved: Object.values(state.transformOverrides).filter(isValid)
      .length,
  };
}
